import React, { useState, useEffect } from 'react'

export default function BluePlaceholder({ label = 'PROJECT-IMG', sub, variant = 'building', height = 320, accent = 'var(--accent)', src }) {
  const [imgOk, setImgOk] = useState(!!src)
  useEffect(() => { setImgOk(!!src) }, [src])

  const variants = {
    building: (
      <>
        <rect x="120" y="60" width="40" height="220" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
        <rect x="170" y="100" width="60" height="180" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
        <rect x="240" y="140" width="50" height="140" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
        {Array.from({ length: 14 }).map((_, i) => (
          <line key={i} x1="120" y1={70 + i*16} x2="290" y2={70 + i*16} stroke="currentColor" strokeWidth="0.3" opacity="0.4" />
        ))}
        <circle cx="320" cy="60" r="24" fill="none" stroke={accent} strokeWidth="0.6" />
        <circle cx="320" cy="60" r="8" fill={accent} opacity="0.4" />
      </>
    ),
    bridge: (
      <>
        <line x1="20" y1="200" x2="380" y2="200" stroke="currentColor" strokeWidth="1" />
        <path d="M40 200 Q200 100 360 200" fill="none" stroke="currentColor" strokeWidth="0.8" />
        <path d="M40 200 Q200 130 360 200" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
        {Array.from({ length: 10 }).map((_, i) => (
          <line key={i} x1={50 + i*32} y1="200" x2={50 + i*32} y2={200 - Math.sin((i+0.5)/10 * Math.PI) * 70} stroke="currentColor" strokeWidth="0.4" />
        ))}
        <circle cx="200" cy="135" r="3" fill={accent} />
      </>
    ),
    interior: (
      <>
        <line x1="40" y1="60" x2="360" y2="60" stroke="currentColor" strokeWidth="0.5" />
        <line x1="40" y1="280" x2="360" y2="280" stroke="currentColor" strokeWidth="0.5" />
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={i} x1={40 + i*45} y1="60" x2={200} y2="170" stroke="currentColor" strokeWidth="0.3" opacity="0.5" />
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={i} x1={40 + i*45} y1="280" x2={200} y2="170" stroke="currentColor" strokeWidth="0.3" opacity="0.5" />
        ))}
        <rect x="160" y="140" width="80" height="60" fill="none" stroke={accent} strokeWidth="0.8" />
      </>
    ),
    map: (
      <>
        {Array.from({ length: 6 }).map((_, i) => (
          <path key={i} d={`M20 ${100 + i*20} Q120 ${60 + i*15} 220 ${110 + i*15} T380 ${100 + i*20}`} fill="none" stroke="currentColor" strokeWidth="0.4" opacity={0.5 - i*0.06} />
        ))}
        <circle cx="220" cy="170" r="14" fill="none" stroke={accent} strokeWidth="1" />
        <circle cx="220" cy="170" r="4" fill={accent} />
        <line x1="220" y1="184" x2="220" y2="240" stroke={accent} strokeWidth="0.6" strokeDasharray="2 2" />
      </>
    ),
    machine: (
      <>
        <rect x="140" y="240" width="80" height="40" fill="none" stroke="currentColor" strokeWidth="0.8" />
        <line x1="180" y1="240" x2="180" y2="80" stroke="currentColor" strokeWidth="0.8" />
        <line x1="180" y1="80" x2="320" y2="80" stroke="currentColor" strokeWidth="0.8" />
        <line x1="180" y1="80" x2="100" y2="120" stroke="currentColor" strokeWidth="0.8" />
        <line x1="280" y1="80" x2="280" y2="180" stroke="currentColor" strokeWidth="0.4" />
        <rect x="270" y="180" width="20" height="14" fill="none" stroke={accent} strokeWidth="0.6" />
        <circle cx="180" cy="270" r="6" fill="none" stroke="currentColor" strokeWidth="0.5" />
      </>
    ),
    site: (
      <>
        {Array.from({ length: 6 }).map((_, r) =>
          Array.from({ length: 8 }).map((_, c) => (
            <rect key={`${r}-${c}`} x={40 + c*42 + r*8} y={60 + r*30} width="38" height="26"
              fill="none" stroke="currentColor" strokeWidth="0.3" opacity={0.6 - r*0.07} />
          ))
        )}
        <rect x="180" y="180" width="60" height="40" fill={accent} opacity="0.15" stroke={accent} strokeWidth="0.6" />
      </>
    ),
  }

  const showImage = src && imgOk
  const labelColor = showImage ? 'rgba(255,255,255,0.92)' : 'var(--fg-2)'
  const subColor = showImage ? 'rgba(255,255,255,0.78)' : 'var(--fg-3)'
  const labelShadow = showImage ? '0 1px 3px rgba(0,0,0,0.6)' : 'none'

  return (
    <div style={{
      position: 'relative', width: '100%', height,
      background: 'var(--bg-1)', overflow: 'hidden',
      border: '1px solid var(--line-soft)',
    }}>
      {showImage && (
        <>
          <img src={src} alt={label} loading="lazy" onError={() => setImgOk(false)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.92) contrast(1.02)' }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(20,20,20,0.35) 0%, rgba(20,20,20,0) 22%, rgba(20,20,20,0) 65%, rgba(20,20,20,0.55) 100%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.18, mixBlendMode: 'screen',
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }} />
        </>
      )}
      {!showImage && (
        <>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'repeating-linear-gradient(135deg, rgba(43,43,43,0.04) 0 1px, transparent 1px 12px)',
          }} />
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', color: 'var(--fg-2)' }} preserveAspectRatio="xMidYMid meet" viewBox="0 0 400 320">
            <g style={{ color: 'var(--fg-1)' }}>{variants[variant] || variants.building}</g>
          </svg>
        </>
      )}

      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} preserveAspectRatio="none" viewBox="0 0 400 320">
        <g stroke={showImage ? 'rgba(255,255,255,0.55)' : 'var(--fg-2)'} strokeWidth="0.6" opacity={showImage ? 0.7 : 0.4}>
          <line x1="0" y1="20" x2="20" y2="20" /><line x1="20" y1="0" x2="20" y2="20" />
          <line x1="380" y1="20" x2="400" y2="20" /><line x1="380" y1="0" x2="380" y2="20" />
          <line x1="0" y1="300" x2="20" y2="300" /><line x1="20" y1="300" x2="20" y2="320" />
          <line x1="380" y1="300" x2="400" y2="300" /><line x1="380" y1="300" x2="380" y2="320" />
        </g>
      </svg>

      <div style={{ position: 'absolute', top: 12, left: 14, fontFamily: 'var(--mono)', fontSize: 9.5, color: labelColor, letterSpacing: '0.15em', textShadow: labelShadow }}>
        {label}
      </div>
      <div style={{ position: 'absolute', top: 12, right: 14, fontFamily: 'var(--mono)', fontSize: 9.5, color: subColor, letterSpacing: '0.15em', textShadow: labelShadow }}>
        {sub || ''}
      </div>
      <div style={{ position: 'absolute', bottom: 12, left: 14, fontFamily: 'var(--mono)', fontSize: 9.5, color: subColor, letterSpacing: '0.15em', textShadow: labelShadow }}>
        SCALE 1:200
      </div>
      <div style={{ position: 'absolute', bottom: 12, right: 14, fontFamily: 'var(--mono)', fontSize: 9.5, color: subColor, letterSpacing: '0.15em', textShadow: labelShadow }}>
        N ↑
      </div>
    </div>
  )
}
